import { FC, ReactNode } from 'react';
import {
	Box,
	Drawer,
	SwipeableDrawer,
	SwipeableDrawerProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { MOBILE_CONTAINER_WIDTH } from '@mopla/constants';

export interface IMplBottomSheetProps {
	open: boolean;
	onOpen?: () => void;
	onClose?: () => void;
	children: ReactNode;
	maxHeight?: string | number;
	disableBackdropClose?: boolean;
	disableSwipeClose?: boolean;
	isStatic?: boolean;
	showPuller?: boolean;
	testId?: string;
	/** Custom z-index for the sheet. Useful when stacking multiple sheets. */
	zIndex?: number;
	/**
	 * Passed through to the underlying MUI Modal. When omitted, MUI's default
	 * kicks in (SwipeableDrawer hardcodes `true`). Pass `false` to drop the
	 * sheet's DOM node on close — useful for portal z-index stacking.
	 */
	keepMounted?: boolean;
}

interface IStyledDrawerProps {
	ownerState: {
		maxHeight?: string | number;
	};
}

const drawerPaperStyles = (maxHeight?: string | number) => ({
	'& .MuiDrawer-paper': {
		borderTopLeftRadius: 16,
		borderTopRightRadius: 16,
		maxHeight: maxHeight ?? '90vh',
		maxWidth: MOBILE_CONTAINER_WIDTH,
		width: '100%',
		margin: '0 auto',
		boxSizing: 'border-box',
		overflow: 'hidden',
		boxShadow: '0px 3px 20px 0px rgba(0, 0, 0, 0.25)',
	},
});

const StyledSwipeableDrawer = styled(SwipeableDrawer, {
	shouldForwardProp: (prop) => prop !== 'ownerState',
})<IStyledDrawerProps>(({ ownerState }) =>
	drawerPaperStyles(ownerState.maxHeight)
);

const StyledDrawer = styled(Drawer, {
	shouldForwardProp: (prop) => prop !== 'ownerState',
})<IStyledDrawerProps>(({ ownerState }) =>
	drawerPaperStyles(ownerState.maxHeight)
);

const Puller = styled(Box)(({ theme }) => ({
	width: 48,
	height: 4,
	backgroundColor: theme.palette.neutral?.[200] ?? '#E0E0E0',
	borderRadius: 2,
	position: 'absolute',
	top: 8,
	left: '50%',
	transform: 'translateX(-50%)',
}));

const ContentWrapper = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'hasPuller',
})<{
	hasPuller: boolean;
}>(({ hasPuller }) => ({
	paddingTop: hasPuller ? 16 : 0,
	paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
	height: '100%',
	overflow: 'auto',
}));

export const MplBottomSheet: FC<IMplBottomSheetProps> = ({
	open,
	onOpen,
	onClose,
	children,
	maxHeight,
	disableBackdropClose = false,
	disableSwipeClose = false,
	isStatic = false,
	showPuller = false,
	testId = 'mpl-bottom-sheet',
	zIndex,
	keepMounted,
}) => {
	const handleClose = () => {
		if (!isStatic && onClose) {
			onClose();
		}
	};

	const handleOpen = () => {
		if (onOpen) {
			onOpen();
		}
	};

	const sheetContent = (
		<>
			{showPuller && !isStatic && <Puller data-testid={`${testId}-puller`} />}
			<ContentWrapper hasPuller={showPuller} data-testid={`${testId}-content`}>
				{children}
			</ContentWrapper>
		</>
	);

	// Use regular Drawer for static mode - completely prevents any dragging
	if (isStatic) {
		return (
			<StyledDrawer
				anchor="bottom"
				open={open}
				hideBackdrop
				variant="persistent"
				ownerState={{ maxHeight }}
				data-testid={testId}
				sx={{
					...(zIndex != null && { zIndex }),
					pointerEvents: 'none',
					'& .MuiDrawer-paper': {
						pointerEvents: 'auto',
					},
				}}
			>
				{sheetContent}
			</StyledDrawer>
		);
	}

	// Use SwipeableDrawer for interactive mode
	const shouldDisableSwipe = disableSwipeClose;

	const drawerProps: SwipeableDrawerProps = {
		anchor: 'bottom',
		open,
		onClose: handleClose,
		onOpen: handleOpen,
		disableSwipeToOpen: true,
		allowSwipeInChildren: !shouldDisableSwipe,
	};

	if (keepMounted !== undefined) {
		drawerProps.ModalProps = { keepMounted };
	}

	if (disableBackdropClose) {
		drawerProps.ModalProps = {
			...drawerProps.ModalProps,
			onClose: (_event: object, reason: string) => {
				if (reason === 'backdropClick') {
					return;
				}
				handleClose();
			},
		};
	}

	if (shouldDisableSwipe) {
		drawerProps.disableDiscovery = true;
		drawerProps.swipeAreaWidth = 0;
		drawerProps.hysteresis = 1;
		drawerProps.minFlingVelocity = Infinity;
	}

	return (
		<StyledSwipeableDrawer
			{...drawerProps}
			ownerState={{ maxHeight }}
			data-testid={testId}
			{...(zIndex != null && { sx: { zIndex } })}
		>
			{sheetContent}
		</StyledSwipeableDrawer>
	);
};
